/* eslint-disable */
import axios from "./axiosWrapper"
import { useOAuthService } from "@/services/OAuthService"
import { loadConfig } from "@/configLoader"
import { Device } from "@capacitor/device"
// ↓↓↓ make sure this path matches your file that exports getDeviceFingerprint()
import { getDeviceFingerprint } from "@/services/BlueIDService"

const oAuth = useOAuthService()

// ───────────────────────────────────────────────────────────────────────────────
// Base URL & headers
// ───────────────────────────────────────────────────────────────────────────────
async function baseUrl() {
  const env = await loadConfig()
  if (!env.analyticsUrl) throw new Error("analyticsUrl missing in envConfig")
  return env.analyticsUrl.endsWith("/") ? env.analyticsUrl : env.analyticsUrl + "/"
}

async function headers() {
  const env = await loadConfig()
  const token = await oAuth.getToken()
  return {
    Authorization: `Bearer ${token}`,
    "x-ps-api-key": env.apiKey,
    "Content-Type": "application/json"
  }
}

// ───────────────────────────────────────────────────────────────────────────────
// Device ID = your stable fingerprint (fallback: platform:osVersion)
// ───────────────────────────────────────────────────────────────────────────────
let cachedDeviceId = null

async function getDeviceId() {
  if (cachedDeviceId) return cachedDeviceId
  try {
    const fp = await getDeviceFingerprint()
    if (fp && typeof fp === "string" && fp.length) {
      cachedDeviceId = fp
      return cachedDeviceId
    }
  } catch (_) {
    // continue to fallback
  }

  try {
    const info = await Device.getInfo()
    const plat = String(info?.platform || "unknown")
    const ver = String(info?.osVersion || "unknown")
    cachedDeviceId = `${plat}:${ver}`
  } catch (_) {
    cachedDeviceId = "unknown:unknown"
  }
  return cachedDeviceId
}

// ───────────────────────────────────────────────────────────────────────────────
// Mappers
// UI → API: use recordedAt (optional) so late/queued points keep real time.
// API → UI: normalize for UI components.
// ───────────────────────────────────────────────────────────────────────────────
const uiToApi = p => ({
  latitude: Number(p.lat ?? p.latitude),
  longitude: Number(p.lon ?? p.longitude),
  accuracy: p.accuracy ?? null,
  speed: p.speed ?? null,
  altitude: p.altitude ?? null,
  heading: p.heading ?? null,
  isMoving: p.isMoving ?? p.is_moving ?? null,
  recordedAt: p.recordedAt ? new Date(p.recordedAt).toISOString() : p.ts ? new Date(p.ts).toISOString() : new Date().toISOString(),
  meta: p.meta ?? undefined
})

const apiToUi = it => ({
  id: it.id || it._id,
  device_id: String(it.deviceId || it.device_id || "Unknown"),
  ts: it.recordedAt || it.timestamp || it.createdAt || it.ts || new Date().toISOString(),
  lat: Number(it.latitude ?? it.lat),
  lon: Number(it.longitude ?? it.lon),
  accuracy: it.accuracy ?? null,
  speed: it.speed ?? null,
  altitude: it.altitude ?? null,
  heading: it.heading ?? null,
  is_moving: it.isMoving ?? it.moving ?? null,
  meta: it.meta || {}
})

// ───────────────────────────────────────────────────────────────────────────────
// POST single location
// ───────────────────────────────────────────────────────────────────────────────
export async function postLocation(point) {
  const deviceId = await getDeviceId()
  const payload = { deviceId, ...uiToApi(point) }
  const url = (await baseUrl()) + "locations"
  const res = await axios({ url, method: "POST", headers: await headers(), data: payload })
  return res?.data
}

// ───────────────────────────────────────────────────────────────────────────────
// POST batch (fan-out single POSTs to keep API contract identical)
// Returns { ok, count }
// ───────────────────────────────────────────────────────────────────────────────
export async function postLocationsBatch(points = []) {
  if (!Array.isArray(points) || points.length === 0) return { ok: true, count: 0 }
  const deviceId = await getDeviceId()
  const url = (await baseUrl()) + "locations"
  const hdr = await headers()
  const payload = points.map(p => ({ deviceId, ...uiToApi(p) }))
  const settled = await Promise.allSettled(payload.map(p => axios({ url, method: "POST", headers: hdr, data: p })))
  const okCount = settled.filter(s => s.status === "fulfilled").length
  return { ok: okCount === payload.length, count: okCount }
}

// ───────────────────────────────────────────────────────────────────────────────
// GET locations (filterable)
// Params: { deviceId?, sinceMinutes?, limit=500, order="desc" }
// ───────────────────────────────────────────────────────────────────────────────
export async function getLocations({ deviceId, sinceMinutes, limit = 500, order = "desc" } = {}) {
  const u = new URL((await baseUrl()) + "locations")
  if (deviceId) u.searchParams.set("deviceId", deviceId)
  if (sinceMinutes) u.searchParams.set("sinceMinutes", String(sinceMinutes))
  if (limit) u.searchParams.set("limit", String(limit))
  if (order) u.searchParams.set("order", order)
  const res = await axios({ url: u.toString(), method: "GET", headers: await headers() })
  const list = Array.isArray(res?.data?.items) ? res.data.items : Array.isArray(res?.data) ? res.data : []
  return list.map(apiToUi)
}

// ───────────────────────────────────────────────────────────────────────────────
// DELETE locations
// Optional: ?deviceId=abc &/or ?olderThanMinutes=1440
// If no query, deletes ALL (use carefully).
// ───────────────────────────────────────────────────────────────────────────────
export async function deleteLocations({ deviceId, olderThanMinutes } = {}) {
  const u = new URL((await baseUrl()) + "locations")
  if (deviceId) u.searchParams.set("deviceId", deviceId)
  if (olderThanMinutes) u.searchParams.set("olderThanMinutes", String(olderThanMinutes))
  const res = await axios({ url: u.toString(), method: "DELETE", headers: await headers() })
  return res?.data ?? { ok: true }
}

// ───────────────────────────────────────────────────────────────────────────────
// Presence = latest record per device within the window
// ───────────────────────────────────────────────────────────────────────────────
export async function getPresence({ sinceMinutes = 1440, limit = 2000 } = {}) {
  const list = await getLocations({ sinceMinutes, limit, order: "desc" })
  const latest = new Map()
  for (const i of list) {
    const key = i.device_id
    const prev = latest.get(key)
    if (!prev || new Date(i.ts) > new Date(prev.ts)) latest.set(key, i)
  }
  return Array.from(latest.values())
}

// ───────────────────────────────────────────────────────────────────────────────
// History for a specific device
// ───────────────────────────────────────────────────────────────────────────────
export async function getHistoryByDevice({ deviceId, sinceMinutes = 1440, limit = 200, order = "desc" } = {}) {
  return await getLocations({ deviceId, sinceMinutes, limit, order })
}

// ───────────────────────────────────────────────────────────────────────────────
// Danger: delete ALL locations (no filters)
// ───────────────────────────────────────────────────────────────────────────────
export async function deleteAllLocations() {
  const u = new URL((await baseUrl()) + "locations")
  const res = await axios({ url: u.toString(), method: "DELETE", headers: await headers() })
  return res?.data ?? { ok: true }
}

export default {
  postLocation,
  postLocationsBatch,
  getLocations,
  deleteLocations,
  getPresence,
  getHistoryByDevice,
  deleteAllLocations
}
